/**
 * Icon — web port that matches the RN component's surface (`name`, `size`,
 * `color`, `style`, `onPress`).
 *
 * Uses Ionicons web components (loaded from CDN in index.html), so the same
 * `name` strings used in RN (`'home-outline'`, `'chevron-forward'`,
 * `'arrow-back'`, etc.) work unchanged.
 */

import type { CSSProperties, FC, MouseEventHandler } from 'react';

type IonIconAttrs = {
  name?: string;
  src?: string;
  ios?: string;
  md?: string;
  size?: 'small' | 'large';
  color?: string;
  class?: string;
  style?: CSSProperties;
  children?: React.ReactNode;
};

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'ion-icon': IonIconAttrs;
    }
  }
}

interface IconProps {
  name: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
  onPress?: () => void;
  className?: string;
}

const Icon: FC<IconProps> = ({
  name,
  size = 24,
  color = '#fff',
  style,
  onPress,
  className,
}) => {
  const wrapperStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: size,
    height: size,
    color,
    fontSize: size,
    cursor: onPress ? 'pointer' : undefined,
    ...style,
  };

  const handleClick: MouseEventHandler<HTMLSpanElement> | undefined = onPress
    ? () => onPress()
    : undefined;

  return (
    <span
      role={onPress ? 'button' : undefined}
      onClick={handleClick}
      style={wrapperStyle}
      className={className}
    >
      <ion-icon name={name} style={{ width: size, height: size, color }} />
    </span>
  );
};

export default Icon;
